import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import qs from 'qs';
import * as routes from '../../routes';
import ActiveErrors from '../../components/common/activeErrors';
import { validateDateOfBirth, validateGuid, validatePhoneNumber } from '../../lib/validation';
import {
	createNewPatient,
	ensurePatient,
	searchPatient,
	setActivePatient,
	startBookingProcess,
	clearAgentInstructions,
	clearBookingContext,
	clearActivePatient,
	attachPatientToDecisionSupportSession,
} from '../../actions/rd2RefactorActions';
import SlotDetailsPanel from '../../components/appointment/slotDetailsPanel';
import { selectAppointmentFirstReservationInContext } from '../../selectors/appointmentFirstSelectors';
import { getPatientPools } from '../../actions/sessionActions';
import { useAppDispatch, useAppSelector } from "../../store";
import PatientSearchForm from '../../components/patient/patientSearchForm';
import PatientSearchResultList from '../../components/patient/patientSearchResultList';
import LoadingIndicator from '../../components/loadingIndicator';
import './patientSearchView.css';

interface PatientPool {
	displayName: string;
	prefix: string;
	integrationUniqueToken: string;
	uniqueToken: string;
	orgId: string | null;
	patientPoolId: number;
}

function PatientSearchView(props: any) {

	const activeCareOrder: any = useAppSelector((state: any) => state.careOrder);
	const activePatient: any = useAppSelector((state: any) => state.activePatient);
	const availabilitySearchConfig: any = useAppSelector((state: any) => state.config.availabilitySearch);
	const decisionSupport: any = useAppSelector((state: any) => state.config.decisionSupport);
	const decisionSupportOutput: any = useAppSelector((state: any) => state.decisionSupport);
	const patientConfig: any = useAppSelector((state: any) => state.config.patient);
	const isSearching: any = useAppSelector((state: any) => state.patientSearch.isSearching);
	const patientList: any = useAppSelector((state: any) => state.patientSearch.patientList);
	const patientSearchFieldConfig: any = useAppSelector((state: any) => state.config.patientSearch);
	const selectedPatient: any = useAppSelector((state: any) => state.patientSearch.selectedPatient);
	const patientPools: PatientPool[] = useAppSelector((state: any) => state.session.patientPools);
	const locationEntryPoint: any = useAppSelector((state: any) => state.session.locationEntryPoint);
	const routePrefix: any = useAppSelector((state: any) => state.config.instance.routePrefix);
	const appointmentFirstReservationInContext: boolean = useAppSelector(
		selectAppointmentFirstReservationInContext,
	);
	const reservation: any = useAppSelector((state: any) => state.appointment.reservation);
	const isPatientPoolLocked = !!(locationEntryPoint?.isPatientPoolLocked && locationEntryPoint?.patientPoolUniqueToken);

	const [error, setError] = useState<string>('');
	const [isLoadingPatientDetails, setIsLoadingPatientDetails] = useState<boolean>(false);
	const [touchedInputs, setTouchedInputs] = useState<any>({});
	const [firstName, setFirstName] = useState<string>('');
	const [lastName, setLastName] = useState<string>('');
	const [dateOfBirth, setDateOfBirth] = useState<string>('');
	const [phoneNumber, setPhoneNumber] = useState<string>('');
	const [memberId, setMemberId] = useState<string>('');
	const [identityValue, setIdentityValue] = useState<string>('');
	const [patientPoolUniqueToken, setPatientPoolUniqueToken] = useState<string>('');
	const [disableRow, setDisableRow] = useState<boolean>(false);
	const [forceValidation, setForceValidation] = useState<boolean>(false);
	const [phoneForceInvalid, setPhoneForceInvalid] = useState<boolean>(false);
	const [patientPoolForceInvalid, setPatientPoolForceInvalid] = useState<boolean>(false);
	const [isHandleBookClickDeferred, setIsHandleBookClickDeferred] = useState<boolean>(false);
	const [isHandleRowClickDeferred, setIsHandleRowClickDeferred] = useState<boolean>(false);
	const [isPatientPoolsLoading, setIsPatientPoolsLoading] = useState<boolean>(patientConfig.enablePatientSearchWithPool);

	const dispatch = useAppDispatch();
	const location = useLocation();

	const onChange = (e: any) => {
		const { name, value } = e.target;
		setTouchedInputs((prevTouched: any) => ({
			...prevTouched,
			[name]: true,
		}));
		switch (name) {
			case 'firstName':
				setFirstName(value);
				break;
			case 'lastName':
				setLastName(value);
				break;
			case 'dateOfBirth':
				setDateOfBirth(value);
				break;
			case 'phoneNumber':
				setPhoneNumber(value);
				validatePhoneInput(value);
				break;
			case 'memberId':
				setMemberId(value);
				break;
			case 'identityValue':
				setIdentityValue(value);
				break;
			case 'patientPoolUniqueToken':
				setPatientPoolUniqueToken(value);
				validatePatientPool(value);
				break;
			default:
				break;
		}
	};

	// Keyed on the query string: navigating here from the menu drops the appointment-first marker
	// without remounting this view, and that arrival still has to clear.
	useEffect(() => {
		clearCache();
	}, [location.search]);

	useEffect(() => {
		const fetchPatientPools = async () => {
			try {
				const response: any = await dispatch(getPatientPools());
				if (response.error) {
					setError('An error occurred while fetching patient pools.');
				}
				setIsPatientPoolsLoading(false);
			}
			catch (error: any) {
			}
		};

		if (patientConfig.enablePatientSearchWithPool) {
			setIsPatientPoolsLoading(true);
			fetchPatientPools();
		}
	}, []);

	useEffect(() => {
		if (isPatientPoolLocked) {
			setPatientPoolUniqueToken(locationEntryPoint.patientPoolUniqueToken);
			setPatientPoolForceInvalid(false);
			return;
		}
		if (patientPools.length === 1)
			setPatientPoolUniqueToken(patientPools[0].uniqueToken);
	}, [patientPools, isPatientPoolLocked, locationEntryPoint?.patientPoolUniqueToken])

	const clearCache = () => {
		// Only the appointment-first handoff arrives mid-flow with a reserved slot worth keeping.
		// Every other arrival clears, which also turns appointment-first mode back off.
		const { appointmentFirst } = qs.parse(location.search, { ignoreQueryPrefix: true });
		if (appointmentFirst) {
			return;
		}

		dispatch(clearBookingContext());
		dispatch(clearAgentInstructions());
	};

	const completeAppointmentFirstPatientSelection = (patient: any) => {
		// Failed requests resolve with an error payload instead of rejecting, so each step has to be
		// checked before the next one. Booking with the decision support session still unattached
		// would lose the reserved slot's context.
		dispatch(setActivePatient(patient.referenceId))
			.then((response: any) => {
				if (response?.error) {
					return Promise.reject(response.error);
				}
				return dispatch(attachPatientToDecisionSupportSession(patient.referenceId));
			})
			.then((response: any) => {
				if (response?.error) {
					return Promise.reject(response.error);
				}
				dispatch(
					routes.routeToBooking(
						routePrefix,
						patient.referenceId,
						reservation.flexCalendarEntryId,
					),
				);
			})
			.catch(() => {
				setDisableRow(false);
			});
	};

	const handleBookClick = (patient: any) => {
		dispatch(clearActivePatient());
		setDisableRow(true);
		if (!validateGuid(patient.referenceId)) {
			setIsHandleBookClickDeferred(true);
			dispatch(ensurePatient(patient)).then((response: any) => {
				if (response.error) {
					setDisableRow(false);
				}
			});
		} else if (appointmentFirstReservationInContext) {
			setIsHandleBookClickDeferred(false);
			completeAppointmentFirstPatientSelection(patient);
		} else {
			setIsHandleBookClickDeferred(false);
			dispatch(setActivePatient(patient.referenceId))
				.then(() => {
					dispatch(
						startBookingProcess(
							activeCareOrder,
							decisionSupportOutput,
							availabilitySearchConfig,
							activePatient.details,
							decisionSupport.useDecisionSupport,
							routePrefix,
						),
					);
				})
				.catch(() => {
					setDisableRow(false);
				});
		}
	};

	const handleCreateClick = (e: any) => {
		e.preventDefault();
		dispatch(
			createNewPatient(
				routePrefix,
				firstName,
				lastName,
				dateOfBirth,
				memberId && !patientList.items.length ? memberId : '',
			),
		);
	};

	const handleRowClick = (patient: any) => {
		dispatch(clearActivePatient());
		setDisableRow(true);
		setIsLoadingPatientDetails(true);
		if (!validateGuid(patient.referenceId)) {
			setIsHandleRowClickDeferred(true);
			dispatch(ensurePatient(patient)).then((response: any) => {
				if (response.error) {
					setDisableRow(false);
					setIsLoadingPatientDetails(false);
				}
			});
		} else {
			setIsHandleRowClickDeferred(false);
			dispatch(setActivePatient(patient.referenceId))
				.then(() => {
					dispatch(routes.patientDetails(routePrefix, patient.referenceId));
				})
				.catch(() => {
					setDisableRow(false);
					setIsLoadingPatientDetails(false);
				});
		}
	};

	const clearSearchCriteria = () => {
		setFirstName('');
		setLastName('');
		setDateOfBirth('');
		setMemberId('');
		setIdentityValue('');
		setForceValidation(false);
		setTouchedInputs({});
		setPhoneNumber('');
		setPhoneForceInvalid(false);
		if (!isPatientPoolLocked) {
			setPatientPoolUniqueToken('');
			setPatientPoolForceInvalid(false);
		}
	};

	const patientSearch = (e: any) => {
		e.preventDefault();

		setForceValidation(true);

		const patientSearchCriteria = {
			firstName: firstName.trim(),
			lastName: lastName.trim(),
			dateOfBirth: dateOfBirth,
			memberId: memberId.trim(),
			identityValue: identityValue.trim(),
			phoneNumber: phoneNumber,
			patientPoolUniqueToken: patientPoolUniqueToken
		};

		if (validateSearchCriteria(patientSearchCriteria)) {
			dispatch(searchPatient(patientSearchCriteria));
		}

		// TODO: handle error response
	};

	const shouldValidate = (name: string) => {
		return forceValidation || touchedInputs[name];
	};

	const validatePhoneInput = (value: string) => {
		let isValid = !value || validatePhoneNumber(value);
		setPhoneForceInvalid(!isValid);
	};

	const validatePatientPool = (value: string) => {
		setPatientPoolForceInvalid(value === '');
	}

	const validateSearchCriteria = (searchCriteria: any) => {
		// if any of the demographics fields are set, we are "using" that panel
		const isUsingDemographics = searchCriteria.firstName || searchCriteria.lastName || searchCriteria.dateOfBirth;
		const isUsingPhone = searchCriteria.phoneNumber;
		let isPhoneValid = !isUsingPhone || (isUsingPhone && validatePhoneNumber(searchCriteria.phoneNumber));
		const isPatientPoolValid = !patientConfig.enablePatientSearchWithPool || (searchCriteria.patientPoolUniqueToken != '');

		if (!isPatientPoolValid) {
			setPatientPoolForceInvalid(!isPatientPoolValid);
		}

		if (!isPhoneValid) {
			setPhoneForceInvalid(!isPhoneValid);
		}

		// if using the demographics panel, verify that field requirements are met
		let isDemographicsValid = true;
		if (isUsingDemographics) {
			if (patientSearchFieldConfig.firstName.isRequired && !searchCriteria.firstName) {
				isDemographicsValid = false;
			}
			if (patientSearchFieldConfig.lastName.isRequired && !searchCriteria.lastName) {
				isDemographicsValid = false;
			}
			if (patientSearchFieldConfig.dateOfBirth.isRequired &&
				(!searchCriteria.dateOfBirth || !validateDateOfBirth(searchCriteria.dateOfBirth))
			) {
				isDemographicsValid = false;
			}
		}
		if (!isPhoneValid || !isPatientPoolValid) {
			isDemographicsValid = false;
		}

		// must be using 1 of the 3 panels, and not have a demographics requiment failure (if applicable)
		return (
			(searchCriteria.memberId || searchCriteria.identityValue || isUsingDemographics || searchCriteria.phoneNumber) &&
			isDemographicsValid
		);
	};

	//TODO: instead of checking during render we should refactor this to use a promise more correctly in (intial) call to handleBookClick and handleRowClick
	if (isHandleBookClickDeferred && selectedPatient) {
		handleBookClick(selectedPatient);
	} else if (isHandleRowClickDeferred && selectedPatient) {
		handleRowClick(selectedPatient);
	}

	return (
		<div>
			<ActiveErrors />
			{appointmentFirstReservationInContext && <SlotDetailsPanel />}
			{!isPatientPoolsLoading ? (
					<>
						<PatientSearchForm
							clearSearchCriteria={clearSearchCriteria}
							dateOfBirth={dateOfBirth}
							enablePatientSearchByDemographics={patientConfig.enablePatientSearchByDemographics}
							enablePatientSearchByIdentity={patientConfig.enablePatientSearchByIdentity}
							enablePatientSearchByMemberId={patientConfig.enablePatientSearchByMemberId}
							enablePatientSearchByPhone={patientConfig.enablePatientSearchByPhone}
							enablePatientSearchWithPool={patientConfig.enablePatientSearchWithPool}
							error={error}
							fieldConfig={patientSearchFieldConfig}
							firstName={firstName}
							identityValue={identityValue}
							isSearching={isSearching}
							lastName={lastName}
							memberId={memberId}
							patientPoolUniqueToken={patientPoolUniqueToken}
							memberIdFieldLabel={patientConfig.memberId.fieldLabel}
							memberIdMaxLength={patientConfig.memberId.maxLength}
							onChange={onChange}
							onSubmit={patientSearch}
							patientIdentityFieldLabelOverride={patientConfig.patientIdentityFieldLabelOverride}
							phoneForceInvalid={phoneForceInvalid}
							phoneNumber={phoneNumber}
							shouldValidate={shouldValidate}
							patientPools={patientPools}
							patientPoolForceInvalid={patientPoolForceInvalid}
							disablePatientPoolSelection={isPatientPoolLocked}
						/>
						{patientList && (
							<div className="patientSearchResultList">
								<PatientSearchResultList
									disableRow={disableRow}
									enablePatientAdd={patientConfig.isAddEnabled && !appointmentFirstReservationInContext}
									genderFieldLabel={props.genderFieldLabel}
									memberIdFieldLabel={patientConfig.memberId.fieldLabel}
									onBookClick={handleBookClick}
									onCreateClick={handleCreateClick}
									onRowClick={handleRowClick}
									patients={patientList}
								/>
							</div>
						)}
					</>
				) : <LoadingIndicator />
			}
		</div>
	);
}

export default PatientSearchView;
